import { Box } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import AppBarButton from '~/components/global/AppBarButton';
import { ASSISTANT_BUTTON_LABEL, ASSISTANT_NAME } from '~/components/otto/branding';
import { OttoAvatar } from '~/components/otto/OttoAvatar';
import { OttoIntroTip, useOttoIntroDismissal } from '~/components/otto/OttoIntroTip';
import { useOtto } from '~/components/otto/OttoProvider';
import { isSessionReady } from '~/components/otto/sessions';
import { useFeatureFlags } from '~/utils/featureFlags';
import { ASK_OTTO, canAccess } from '~/utils/role';

type OttoOrientation = 'vertical' | 'horizontal';

function OttoHeaderButtonInner({ orientation = 'vertical' }: { orientation?: OttoOrientation }) {
  const { state, openDock, newChat } = useOtto();
  const { track } = useAnalytics();
  const flags = useFeatureFlags();
  const location = useLocation();
  // When the doc is closed, the active session also counts — the user
  // can't see the conversation, so a finished reply is "ready" for them
  // even if it belongs to the session they last had open.
  const anyReady = useMemo(
    () =>
      state.sessionOrder.some((id) => {
        const s = state.sessions[id];
        if (!s) return false;
        return isSessionReady(s);
      }),
    [state.sessionOrder, state.sessions],
  );
  const { dismissed: introDismissed, dismiss: dismissIntro } = useOttoIntroDismissal();
  // A callback ref rather than useRef: the tip's Popper needs a re-render once the
  // anchor element exists, or it has nothing to hang from on first paint.
  const [introAnchor, setIntroAnchor] = useState<HTMLElement | null>(null);
  const anchorOnButton = useCallback((node: HTMLElement | null) => {
    // Anchor on the button, not the wrapper: the wrapper is a layout box taller than the
    // button, so hanging the caret off it would aim at the bar's padding.
    const button = node?.firstElementChild;
    setIntroAnchor(button instanceof HTMLElement ? button : node);
  }, []);

  if (state.mode !== 'closed') return null;

  const onClick = () => {
    // Clicking through answers the question the tip was there to answer, so it is spent.
    dismissIntro();
    // Both branches transition mode from 'closed' to 'dock' — newChat()
    // mints + opens, openDock() reopens the existing session. Either
    // way the FAB caused a panel-open, so chat_panel_opened fires once
    // regardless of which branch runs.
    track('chat_fab_clicked', { route: location.pathname });
    track('chat_panel_opened', {
      source: 'fab',
      feature_flag_ai_chat: Boolean(flags.aiChat),
    });
    if (!state.activeSessionId) newChat();
    else openDock();
  };

  const aria = anyReady
    ? `Open ${ASSISTANT_NAME}, your portfolio assistant — new answer ready`
    : `Open ${ASSISTANT_NAME}, your portfolio assistant`;

  return (
    <>
      <Box
        ref={anchorOnButton}
        sx={{
          display: 'inline-flex',
          ...(orientation === 'horizontal' ? { width: '100%' } : undefined),
        }}
      >
        <AppBarButton
          orientation={orientation}
          onClick={onClick}
          aria-label={aria}
          outlined
          icon={<OttoAvatar size={20} withReadyDot={anyReady} />}
          label={ASSISTANT_BUTTON_LABEL}
        />
      </Box>
      <OttoIntroTip anchorEl={introAnchor} open={!introDismissed} onDismiss={dismissIntro} />
    </>
  );
}

export function OttoHeaderButton({ orientation = 'vertical' }: { orientation?: OttoOrientation }) {
  const flags = useFeatureFlags();
  const { userDetails } = useContext(UserContext);
  // Otto is consultant-only. Defense-in-depth alongside the backend's
  // IsConsultant permission on /api/v1/genui/chat — if the LD flag is
  // enabled for a Client or Service Provider, we still hide the entry
  // point so they don't see a button that 403s on click.
  if (!flags.aiChat) return null;
  if (!canAccess(userDetails.role, ASK_OTTO)) return null;
  return <OttoHeaderButtonInner orientation={orientation} />;
}
